import type { ApprovalStage } from '@/types/approvalWorkflow';
import { CompactStageCard } from './CompactStageCard';
import { Info } from 'lucide-react';

interface WorkflowListViewProps {
  stages: ApprovalStage[];
  onEditStage: (stage: ApprovalStage) => void;
  showPlaceholderInfo?: boolean;
}

export function WorkflowListView({ stages, onEditStage, showPlaceholderInfo = false }: WorkflowListViewProps) {
  if (!stages || stages.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center max-w-md">
          {/* Illustration */}
          <div className="mb-6 flex justify-center">
            <div className="relative">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                <Info className="h-12 w-12 text-primary/40" />
              </div>
              <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg animate-pulse">
                <span className="text-white font-bold text-sm">+</span>
              </div>
            </div>
          </div>
          
          {/* Text */}
          <h3 className="text-lg font-semibold mb-2">No Stages Yet</h3>
          <p className="text-muted-foreground text-sm mb-4">
            Get started by adding your first approval stage to build your workflow.
          </p>
          
          {/* Helpful Tips */}
          <div className="bg-muted/50 rounded-lg p-4 text-left space-y-2">
            <p className="text-xs font-medium text-muted-foreground">💡 Quick Tips:</p>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• Each stage represents an approval step</li>
              <li>• Add approvers and rules to each stage</li>
              <li>• Stages are processed in order</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {showPlaceholderInfo && (
        <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-1">About Stage References</p>
            <p>
              Only <strong>created stages</strong> appear in this list. If you've referenced a stage in your rules 
              that doesn't exist yet (a "forward reference"), it won't show here until you create it. 
              Check the warnings above to see if any stages need to be created.
            </p>
          </div>
        </div>
      )}
      
      <div className="space-y-0">
        {stages.map((stage, index) => (
          <CompactStageCard
            key={stage.id || index}
            stage={stage}
            stageNumber={index + 1}
            isLastStage={index === stages.length - 1}
            onEdit={onEditStage}
          />
        ))}
      </div>
    </div>
  );
}
